'use client';

import { VoteType } from '@prisma/client';
import { motion } from 'framer-motion';
import { Trash2 } from 'lucide-react';
import Image from 'next/image';
import { memo, useCallback } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../../components/ui/alert-dialog';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { PlaceSeed } from '../../../types/shared';
import { InteractiveCard } from './shared/InteractiveCard';

interface PlaceCardProps {
  place: PlaceSeed;
  isAdmin: boolean;
  index: number;
  onViewDetails: (placeId: string, scrollToComments?: boolean) => void; // ✅ изменили
  onDelete: (placeId: string) => void;
  onVote: (placeId: string, voteType: VoteType) => void;
}

export const PlaceCard = memo(function PlaceCard({
  place,
  isAdmin,
  index,
  onViewDetails,
  onDelete,
  onVote,
}: PlaceCardProps) {
  const isPriority = index < 2;

  const openDetails = useCallback(
    (scrollToComments?: boolean) => onViewDetails(place.id, scrollToComments),
    [onViewDetails, place.id],
  );

  return (
    <article
      className="grid grid-cols-1 xl:grid-cols-2 gap-8 bg-black/10 dark:bg-transparent rounded-2xl p-4 sm:p-6 shadow-lg"
      aria-labelledby={`place-${place.id}-title`}
    >
      <motion.div
        layoutId={`place-${place.id}`}
        className={cn(
          'relative rounded-lg overflow-hidden group cursor-pointer w-full',
          'aspect-video xl:aspect-auto xl:h-full',
          index % 2 === 1 && 'xl:order-2',
        )}
        role="button"
        aria-label={`Посмотреть детали: ${place.title}`}
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && openDetails(false)}
        onClick={() => openDetails(false)}
      >
        <Image
          src={place.imageUrl}
          alt={`Фото места: ${place.title}`}
          fill
          sizes="(max-width: 1280px) 100vw, 50vw"
          className="object-cover object-center transition-transform group-hover:scale-105"
          priority={isPriority}
          loading={isPriority ? 'eager' : 'lazy'}
          quality={75}
        />
      </motion.div>

      <div className="flex flex-col gap-3 min-h-[320px]">
        <div className="flex justify-between items-start gap-4">
          <h3
            id={`place-${place.id}-title`}
            className="text-xl sm:text-2xl font-bold text-main dark:text-sky-400 drop-shadow-md"
          >
            {place.title}
          </h3>

          {isAdmin && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button
                  variant="destructive"
                  size="icon"
                  className="flex-shrink-0"
                  aria-label={`Удалить карточку: ${place.title}`}
                >
                  <Trash2 className="h-4 w-4" aria-hidden="true" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Вы уверены?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Это действие невозможно отменить. Карточка «{place.title}» будет удалена.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Отмена</AlertDialogCancel>
                  <AlertDialogAction onClick={() => onDelete(place.id)}>Удалить</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>

        {place.description && (
          <p className="flex-1 text-sm sm:text-base text-gray-200 dark:text-slate-300 leading-relaxed">
            {place.description}
          </p>
        )}

        {place.details && (
          <p className="text-sm p-3 rounded-md mt-2 bg-black/20 text-gray-200 dark:bg-slate-800/50 dark:text-slate-400">
            {place.details}
          </p>
        )}

        {place.howToGet && (
          <p className="text-sm p-3 rounded-md mt-2 bg-black/20 text-gray-200 dark:bg-slate-800/50 dark:text-slate-400">
            <strong>Как добраться:</strong> {place.howToGet}
          </p>
        )}

        <div className="mt-auto pt-4 border-t border-white/20 dark:border-slate-700">
          <InteractiveCard item={place} onViewDetails={openDetails} onVote={onVote} />
        </div>
      </div>
    </article>
  );
});
